import React, { useEffect, useState, useCallback } from "react";
import "./GroupLoanSingleEmiPay.css";
import SelectComponent from "react-select"; 
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

const API_URL = "https://payyug.in/api";

const GroupLoanSingleEmiPay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.userProfile);

  // Dropdown options states
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loans, setLoans] = useState([]);
  
  // Selected tracking states
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);

  // EMI and layout states
  const [lastPaidEmi, setLastPaidEmi] = useState(null);
  const [currentPendingEmi, setCurrentPendingEmi] = useState(null);
  const [fullLoanEmiData, setFullLoanEmiData] = useState([]);
  const [totalInstallments, setTotalInstallments] = useState(0);

  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const [formData, setFormData] = useState({
    amount: "",
    penaltyAmount: 0,
    overdueDays: 0,
    perDayPenalty: 0,
    totalPayable: "",
    paymentMode: "CASH",
    transactionDate: new Date().toISOString().split("T")[0],
    narration: "",
  });

  // 1. Fetch user profile on mount
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // 2. Fetch Centers when profile ID becomes available
  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(`${API_URL}/group/centre?LoanofficerId=${profile.id}&limit=10000`);
        setCenters(res.data?.data || []);
      } catch (error) {
        console.error("Center fetch error:", error);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  // 3. Fetch Groups when Center changes
  useEffect(() => {
    if (!selectedCenter) {
      setGroups([]);
      return;
    }

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter.value}&limit=10000`
        );
        setGroups(res.data?.data || []);
      } catch (error) {
        console.error("Group fetch error:", error);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  // 4. Fetch Loans list only when both Center and Group are selected
  const fetchLoansList = useCallback(async () => {
    if (!profile?.superAdminId || !selectedCenter?.value || !selectedGroup?.value) {
      setLoans([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get("https://payyug.in/api/group-loan/group/list", {
        params: {
          superAdminId: profile.superAdminId,
          officerId: profile.id,
          groupId: selectedGroup.value,   // Passing ID value
          centerId: selectedCenter.value, // Passing ID value
          limit: 100000,
        },
      });
      const filteredLoan = (res.data?.data || []).filter(
        (l) => l.status?.toLowerCase() !== "closed"
      );
      setLoans(filteredLoan);
    } catch (err) {
      console.error("Error fetching loans:", err);
    } finally {
      setLoading(false);
    }
  }, [profile, selectedCenter, selectedGroup]);

  useEffect(() => {
    if (profile) fetchLoansList();
  }, [profile, fetchLoansList]);

  // 5. Fetch EMI Details when a specific Loan is selected
  const fetchEmiDetails = useCallback(async (loanId) => {
    if (!profile?.superAdminId || !loanId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/group-loan/emi/list-filter`, {
        params: {
          superAdminId: profile.superAdminId,
          branchId:profile?.branchId,
          grouploanAccountId: loanId,
        },
      });
      const loanEmiData = res.data.data || [];
      setFullLoanEmiData(loanEmiData);
      setTotalInstallments(res.data.totalRecords || 0);

      const pending = [...loanEmiData]
        .sort((a, b) => a.installmentNo - b.installmentNo)
        .find((item) => item.status.toLowerCase() === "pending");

      setCurrentPendingEmi(pending);

      if (pending) {
        const penalty = Number(pending.penaltyAmount || 0);
        const baseEmi = Number(pending.emiAmount || 0);
        const days = Number(pending.overdueDays || 0);
        const perDay = Number(pending.perDayPenalty || 0);

        setFormData((prev) => ({
          ...prev,
          amount: baseEmi,
          penaltyAmount: penalty,
          overdueDays: days,
          perDayPenalty: perDay,
          totalPayable: baseEmi + penalty,
        }));
      } else {
        resetFormOnSearch();
      }

      const paid = [...loanEmiData]
        .sort((a, b) => b.installmentNo - a.installmentNo)
        .find((item) => item.status.toLowerCase() === "paid");
      setLastPaidEmi(paid);
    } catch (err) {
      toast.error("Failed to load EMI schedule");
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (selectedLoan?.id) {
      fetchEmiDetails(selectedLoan.id);
    } else {
      setFullLoanEmiData([]);
      setCurrentPendingEmi(null);
      setLastPaidEmi(null);
      setTotalInstallments(0);
    }
  }, [selectedLoan, fetchEmiDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const navigateToReceipt = (emiObj) => {
    if (!emiObj || !fullLoanEmiData) return;
    const nextEmi = fullLoanEmiData.find((item) => item.installmentNo === emiObj.installmentNo + 1);
    navigate("/grouploan/grouploanreport/GroupLoanEmiDetails/reciept", {
      state: { loan: selectedLoan, emi: emiObj, nextEmi: nextEmi || null },
    });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!selectedLoan) return toast.warn("Please select a Loan Account");
    if (!currentPendingEmi) return toast.error("No pending installments found");
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    try {
      setPayLoading(true);
      const { totalPayable, paymentMode, transactionDate, narration, penaltyAmount } = formData;
      const loanId = selectedLoan.id;
      const emiId=currentPendingEmi.id;
      const emiNo = currentPendingEmi.installmentNo;

      const payload = {
        emiId: emiId,
        paidAmount: Number(totalPayable),
        // penaltyAmount: Number(penaltyAmount),
        paymentMode: paymentMode,
        paidDate: transactionDate,
        collectedBy:profile?.id|| localStorage.getItem("userId") || 1,
        remarks: narration || `EMI ${emiNo} Full Payment with Penalty`,
      };

      const res = await axios.post(
        `${API_URL}/group-loan/emi/pay`,
        payload
      );

      toast.success(res.data.message || "EMI Payment Successful!");
      setShowPreview(false);
      
      const refreshRes = await axios.get(`${API_URL}/group-loan/emi/list-filter`,{
        params:{
        grouploanAccountId:loanId
      }
      });
      const updatedEmis = refreshRes.data.emis || [];
      const justPaidEmi = updatedEmis.find((item) => item.installmentNo === emiNo);
      const nextEmi = updatedEmis.find((item) => item.installmentNo === emiNo + 1);

      navigate("/grouploan/grouploanreport/GroupLoanEmiDetails/reciept", {
        state: {
          loan: selectedLoan,
          emi: justPaidEmi,
          nextEmi: nextEmi || null,
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "An error occurred");
    } finally {
      setPayLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!totalInstallments) return 0;
    const paidCount = lastPaidEmi?.installmentNo || 0;
    return (paidCount / totalInstallments) * 100;
  };

  const resetFormOnSearch = () => {
    setFormData((prev) => ({
      ...prev,
      amount: "",
      penaltyAmount: 0,
      overdueDays: 0,
      perDayPenalty: 0,
      totalPayable: ""
    }));
  };

  return (
    <div className="gl-single-loan-portal-container">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width:"100%",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Single Emi Pay
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>

      <form className="gl-single-loan-form-card" onSubmit={handlePreview}>
        <h4 className="gl-single-loan-section-header">
          <i className="fa-solid fa-calendar-check"></i> EMI Schedule Summary
        </h4>

        {selectedLoan && (
          <div className="gl-single-loan-scheme-banner">
            <h4 className="banner-title">
              <i className="fa-solid fa-circle-info"></i> Loan Details
            </h4>
            <div className="gl-single-loan-scheme-grid">
              <div className="scheme-cell">
                <span>Center</span> <strong>{selectedLoan?.centerName}</strong>
              </div>
              <div className="scheme-cell">
                <span>Group</span> <strong>{selectedLoan.groupName}</strong>
              </div>
              <div className="scheme-cell">
                <span>Member</span> <strong>{selectedLoan.memberName}</strong>
              </div>
              <div className="scheme-cell">
                <span>Account No</span> <strong>{selectedLoan.loanAccountNumber}</strong>
              </div>
              <div className="scheme-cell">
                <span>Loan Amount</span> <strong>{selectedLoan.loanAmount}</strong>
              </div>
              <div className="scheme-cell">
                <span>Loan Type</span> <strong>{selectedLoan.loanType}</strong>
              </div>
              <div className="scheme-cell">
                <span>Roi</span> <strong>{selectedLoan.loanRoi}%</strong>
              </div>
              <div className="scheme-cell">
                <span>Frequency</span> <strong>{selectedLoan.loanRecoveryFrequency}</strong>
              </div>
              <div className="scheme-cell">
                <span>Status</span>
                <strong className={`status-label status-${selectedLoan.status?.toLowerCase()}`}>
                  {selectedLoan.status}
                </strong>
              </div>
            </div>
          </div>
        )}

        <div className="gl-single-loan-progress-section">
          <label className="input-field-label">Payment Progress</label>
          <div className="custom-progress-container">
            <div className="custom-progress-fill" style={{ width: `${calculateProgress()}%` }}></div>
            <span className="custom-progress-text">
              {lastPaidEmi?.installmentNo || 0} / {totalInstallments || 0} Installments Paid
            </span>
          </div>
        </div>

        <div className="gl-single-loan-form-inputs">
          <div className="input-row-grid">
            {/* 1. Center Filter */}
            <div className="field-container">
              <label className="input-field-label">Center Search</label>
              <SelectComponent
                className="loan-react-select"
                placeholder="Select Center..."
                value={selectedCenter}
                options={centers.map((center) => ({
                  value: center.id,
                  label: center.CenterName || `Center ${center.id}`,
                  centerData: center,
                }))}
                onChange={(selected) => {
                  setSelectedCenter(selected);
                  setSelectedGroup(null); // Clear dependent child
                  setSelectedLoan(null);  // Clear dependent grandchild
                  resetFormOnSearch();
                }}
                isSearchable
              />
            </div>

            {/* 2. Group Filter */}
            <div className="field-container">
              <label className="input-field-label">Group Search</label>
              <SelectComponent
                className="loan-react-select"
                placeholder="Select Group..."
                value={selectedGroup}
                options={groups.map((g) => ({
                  value: g.id,
                  label: `${g?.groupname} | ${g.groupcode}`,
                  groupData: g,
                }))}
                onChange={(selected) => {
                  setSelectedGroup(selected);
                  setSelectedLoan(null); // Clear dependent child
                  resetFormOnSearch();
                }}
                isSearchable
                isDisabled={!selectedCenter}
              />
            </div>

            {/* 3. Loan Account Filter */}
            <div className="field-container">
              <label className="input-field-label">Loan Account Search</label>
              <SelectComponent
                className="loan-react-select"
                placeholder="Search Account..."
                value={selectedLoan ? { value: selectedLoan.id, label: `${selectedLoan?.memberName} | ${selectedLoan.loanAccountNumber}` } : null}
                options={loans.map((loan) => ({
                  value: loan.id,
                  label: `${loan?.memberName} | ${loan.loanAccountNumber} | ${loan?.loanType}`,
                  loanAccount: loan,
                }))}
                onChange={(selected) => {
                  resetFormOnSearch();
                  setSelectedLoan(selected?.loanAccount || null);
                }}
                isSearchable
                isDisabled={!selectedGroup || loading}
              />
            </div>

            {/* Last Receipt Action */}
            <div className="field-container">
              <label className="input-field-label">Last Receipt</label>
              <div className="input-group-with-btn">
                <input
                  className="input-readonly"
                  type="text"
                  value={lastPaidEmi ? `EMI #${lastPaidEmi.installmentNo} Paid` : "No Record"}
                  readOnly
                />
                {lastPaidEmi && (
                  <button type="button" className="btn-icon-print" onClick={() => navigateToReceipt(lastPaidEmi)}>
                    <i className="fa-solid fa-print"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="input-row-grid">
            <div className="field-container">
              <label className="input-field-label">Upcoming Due Date</label>
              <input
                className="input-readonly highlight-due"
                type="text"
                value={
                  currentPendingEmi
                    ? new Date(currentPendingEmi.dueDate).toLocaleDateString("en-IN")
                    : totalInstallments > 0 && lastPaidEmi?.installmentNo === totalInstallments
                    ? "COMPLETED"
                    : "N/A"
                }
                readOnly
              />
            </div>
            <div className="field-container">
              <label className="input-field-label">Base EMI Amount</label>
              <input
                className="input-readonly"
                type="text"
                value={currentPendingEmi ? `₹ ${currentPendingEmi.emiAmount}` : "₹ 0.00"}
                readOnly
              />
            </div>
          </div>

          {/* Penalty Breakdown Box */}
          {formData.penaltyAmount > 0 && (
            <div className="penalty-breakdown-card">
              <div className="penalty-card-header">
                <i className="fa-solid fa-triangle-exclamation"></i> Late Payment Overdue Details
              </div>
              <div className="penalty-card-body">
                <div className="p-item">
                  <span>Overdue Days</span>
                  <strong>{formData.overdueDays} Days</strong>
                </div>
                <div className="p-item">
                  <span>Penalty/Day</span>
                  <strong>₹ {formData.perDayPenalty}</strong>
                </div>
                <div className="p-item p-highlight">
                  <span>Total Penalty</span>
                  <strong>₹ {formData.penaltyAmount}</strong>
                </div>
              </div>
            </div>
          )}

          <div className="custom-divider"></div>

          <div className="input-row-grid">
            <div className="field-container">
              <label className="input-field-label">Calculated Penalty (INR)</label>
              <input
                className={`input-readonly ${formData.penaltyAmount > 0 ? "penalty-active-box" : ""}`}
                type="text"
                value={`₹ ${formData.penaltyAmount}`}
                readOnly
              />
            </div>
            <div className="field-container">
              <label className="input-field-label">Total Collection Amount</label>
              <input
                className="input-readonly final-collection-highlight"
                type="text"
                value={formData.totalPayable ? `₹ ${formData.totalPayable}` : "₹ 0.00"}
                readOnly
              />
            </div>
          </div>

          <div className="input-row-grid">
            <div className="field-container">
              <label className="input-field-label">Payment Mode</label>
              <select className="custom-select-input" name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                <option value="CASH">💵 CASH</option>
                <option value="UPI">📱 UPI</option>
                <option value="RTGS">🏦 NEFT/RTGS</option>
              </select>
            </div>
            <div className="field-container">
              <label className="input-field-label">Collection Date</label>
              <input className="custom-date-input" type="date" name="transactionDate" value={formData.transactionDate} onChange={handleChange} />
            </div>
          </div>

          <div className="field-container">
            <label className="input-field-label">Remarks</label>
            <input
              className="custom-text-input"
              type="text"
              name="narration"
              value={formData.narration}
              onChange={handleChange}
              placeholder="Optional narration"
            />
          </div>
        </div>

        <div className="form-submit-section">
          <button className="btn-confirm-pay" type="submit" disabled={!currentPendingEmi || loading}>
            {loading ? "Syncing..." : "Process Payment & Generate Receipt"}
          </button>
        </div>
      </form>

      {showPreview && (
        <div className="modal-overlay-bg">
          <div className="preview-modal-box">
            <h3 className="modal-header-title">Confirm Collection</h3>
            <div className="modal-body-content">
              <div className="modal-info-row">
                <span>Customer Name:</span> <strong>{selectedLoan?.userName || selectedLoan?.memberName}</strong>
              </div>
              <div className="modal-info-row">
                <span>Installment No:</span> <strong>{currentPendingEmi?.installmentNo}</strong>
              </div>

              <div className="modal-divider-light"></div>

              <div className="modal-info-row">
                <span>Base EMI Amount:</span> <strong>₹ {formData.amount}</strong>
              </div>

              {formData.penaltyAmount > 0 && (
                <div className="modal-info-row text-danger-penalty">
                  <span>Late Fee ({formData.overdueDays} Days):</span>
                  <strong>+ ₹ {formData.penaltyAmount}</strong>
                </div>
              )}

              <div className="modal-info-row total-payable-row">
                <span>Net Payable:</span> <strong>₹ {formData.totalPayable}</strong>
              </div>
            </div>
            <div className="modal-action-footer">
              <button className="btn-modal-cancel" onClick={() => setShowPreview(false)}>
                Edit
              </button>
              <button className="btn-modal-submit" onClick={handleSubmit} disabled={payLoading}>
                {payLoading ? "Processing..." : "Confirm Pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupLoanSingleEmiPay;